import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "./api";
import { Lion } from "./Lion";
import "./PauseOverlay.css";

// Pause class (decision 68, Q): while the teacher has the class paused, a student's whole screen is
// Roary and the teacher's message. Polls every 10 s (and when the tab comes back to the front), so
// it appears and goes away within 10 s. The server refuses helper turns meanwhile, so this is the
// friendly face of the pause, not the lock.

const POLL_MS = 10_000;

export function PauseOverlay() {
  const [message, setMessage] = useState<string | null>(null); // null: not paused
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let alive = true;
    const check = () => {
      api.classStatus(document.visibilityState === "visible").then(
        (r) => alive && setMessage(r.paused ? r.message || "Eyes up front!" : null),
        () => {}, // a failed poll keeps what's showing; the next one tries again
      );
    };
    check();
    const timer = window.setInterval(check, POLL_MS);
    const onVisible = () => document.visibilityState === "visible" && check();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // While paused, everything behind the overlay is out of reach (no typing or tabbing into it).
  const paused = message !== null;
  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;
    root.inert = paused;
    if (paused) heading.current?.focus();
    return () => {
      root.inert = false;
    };
  }, [paused]);

  if (!paused) return null;
  return createPortal(
    <div className="pause-overlay" role="alertdialog" aria-modal="true" aria-labelledby="pause-message" aria-describedby="pause-note">
      <div className="pause-card">
        <span className="eyebrow gold">Paused by your teacher</span>
        <Lion pose="stand" size={156} label="Roary the lion" bob />
        <h1 id="pause-message" ref={heading} tabIndex={-1}>
          {message}
        </h1>
        <p id="pause-note">Your work is saved. The helper is taking a short break. This screen goes away when your teacher is ready.</p>
      </div>
    </div>,
    document.body,
  );
}
